import { Classes, Colors, Dialog, Intent, ButtonGroup } from "@blueprintjs/core";
import { ButtonContainer, FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { ClubContactApi, ClubContactGridDto, ClubContactSearch } from "../../../../../api";
import { ERoutes } from "../../../../../ERoutes";
import { CustomDataTable, DeleteButton, EditButton, EmailButton, ExportButton } from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { useApiService, useDeleteApi, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { ClubContactPanelStack } from "./ClubContactPanelStack";

export interface IClubContactPageProps {
  clubId: number;
  editMode: boolean;
}

export const ClubContactPage: React.FunctionComponent<IClubContactPageProps> = ({ clubId, editMode }) => {
  const api = useApiService(ClubContactApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [sobjState, setsobjState] = React.useState<any>();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { fullName: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: ClubContactSearch) => {
      setsobjState(ClubContactSearch.fromJS({ ...sObj, clubId: clubId, languageId: currentLanguage.languageId }));
      return api.baseSearch(
        ClubContactSearch.fromJS({ ...sObj, clubId: clubId, languageId: currentLanguage.languageId })
      );
    },
    [api, clubId, currentLanguage.languageId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const { deleteItem } = useDeleteApi<number>(
    React.useMemo(
      () => ({
        deleteApiFn: (data) => api.deleteClubContact(data),
        onDeletedFn: () => search(),
      }),
      [api, search]
    )
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: ClubContactGridDto) => (
          <ButtonContainer>
            <DeleteButton
              minimal={true}
              onDelete={() => {
                deleteItem(row.clubContactId);
              }}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Name),
        fieldName: "fullName",
        render: (row: ClubContactGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.fullName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Language),
        fieldName: "language",
      },
      {
        header: () => t(ETLCodes.Email),
        fieldName: "email",
        render: (value: ClubContactGridDto) => <EmailButton email={value.email} />,
      },
      {
        header: () => t(ETLCodes.PhoneNumber),
        fieldName: "phoneNumber",
      },
      {
        header: () => t(ETLCodes.MobilePhoneNumber),
        fieldName: "mobilePhoneNumber",
      },
      {
        header: () => t(ETLCodes.Role),
        fieldName: "role",
      },
    ],
    [deleteItem, editMode, t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);

  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);
  return (
    <>
      <FieldSet
        title={t(ETLCodes.Contacts)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {editMode && (
              <EditButton
                onClick={(e) => {
                  e.stopPropagation();
                  addItemFunction();
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
      <Dialog
        title={t(ETLCodes.SelectContact)}
        isOpen={dialogOpened}
        onClose={() => {
          setDialogOpened(false);
          search();
        }}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1000, height: 590, background: Colors.WHITE }}
        className={Classes.DIALOG}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <ClubContactPanelStack
            clubId={clubId}
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
          />
        </div>
      </Dialog>
    </>
  );
};
